import { add, endOfDay, Interval, startOfDay } from 'date-fns'
import React, { useCallback, useEffect, useLayoutEffect, useMemo, useState } from 'react'
import { Trans, useTranslation } from 'react-i18next'
import { useLocation, useNavigate, useParams } from 'react-router-dom'

import { Info } from '@mui/icons-material'
import { TabContext, TabPanel } from '@mui/lab'
import { Box, Card, CardContent, Divider, Grid, Typography } from '@mui/material'

import ConfirmDialog from '../../components/ConfirmDialog/ConfirmDialog'
import { DateRangeChangeReason, DateRangeValue } from '../../components/DateRangePicker/DateRangePicker'
import GRCircularProgress from '../../components/GRCircularProgress/GRCircularProgress'
import GRTabNavigation from '../../components/GRTabNavigation/GRTabNavigation'
import { GamePerformaceDialogData, GamePerformanceDialog } from '../../components/GamePerformanceDialog/GamePerformanceDialog'
import { LockedFeature } from '../../components/LockedFeature/LockedFeature'
import { PerformanceChartV2DataType } from '../../components/PerformanceChartV2/PerformanceChartV2DataType'
import ShareUrlButton from '../../components/ShareUrlButton/ShareUrlButton'
import { useInternalAccessCheck, useLiveEventsTrackerAccessCheck } from '../../features/account/hooks/roleHooks'
import { LiveEventsGameSelector } from '../../features/live-events/components/LiveEventGameSelector/LiveEventsGameSelector'
import {
  LiveEventsCalendarByEventTypeContainer,
  calendarId as compareEventsCalendarId,
} from '../../features/live-events/components/LiveEventsCalendar/LiveEventsCalendarByEventTypeContainer/LiveEventsCalendarByEventTypeContainer'
import LiveEventsCalendarList from '../../features/live-events/components/LiveEventsCalendarList/LiveEventsCalendarList'
import { LiveEventsEventStatistics } from '../../features/live-events/components/LiveEventsEventStatistics/LiveEventsEventStatistics'
import LiveEventsFeed from '../../features/live-events/components/LiveEventsFeed/LiveEventsFeed'
import { LiveEventsFeedFilters } from '../../features/live-events/components/LiveEventsFeedFilters/LiveEventsFeedFilters'
import { LiveEventsFilters, LiveEventsFiltersConfig } from '../../features/live-events/components/LiveEventsFilters/LiveEventsFilters'
import LiveEventsGameAlertsList from '../../features/live-events/components/LiveEventsGameAlertsList/LiveEventsGameAlertsList'
import { LiveEventsViewFilters } from '../../features/live-events/components/LiveEventsViewFilters/LiveEventsViewFilters'
import { PerformanceDialogEventsContainer } from '../../features/live-events/components/PerformanceDialogEventsContainer/PerformanceDialogEventsContainer'
import {
  lockedLiveEventsTrackerStartTimestamp,
  lockedLiveEventsTrackerEndTimestamp,
  defaultTrackedEventsStartTimestamp,
} from '../../features/live-events/const/const'
import { useLiveEventsSelectedGameIdsSetting, useUpdateLiveEventsSelectedGameIdsSetting } from '../../features/live-events/hooks/liveEventsSelectedGamesHooks'
import { EventDialogTab } from '../../features/live-events/hooks/useEventDialogTabs'
import { useFreeLiveEventsGamesArray } from '../../features/live-events/hooks/useFreeLiveEventsGamesArray'
import { useLiveEventModal } from '../../features/live-events/hooks/useLiveEventModal'
import { useLiveEventsAnalystNotesModal } from '../../features/live-events/hooks/useLiveEventsAnalystNotesModal'
import { useLiveEventsAnalystOverviewModal } from '../../features/live-events/hooks/useLiveEventsAnalystOverviewModal'
import { useLiveEventsByCalendarTimeRanges } from '../../features/live-events/hooks/useLiveEventsByCalendarTimeRanges'
import {
  useLiveEventsCalendarBlueprintClickHandler,
  useLiveEventsCalendarGroupClickHandler,
} from '../../features/live-events/hooks/useLiveEventsCalendarGroupClickHandler'
import { useLiveEventsCommonFilters } from '../../features/live-events/hooks/useLiveEventsCommonFilters'
import { useLiveEventsFilteredData } from '../../features/live-events/hooks/useLiveEventsFilteredData'
import { useLiveEventsPickerMaxDate } from '../../features/live-events/hooks/useLiveEventsPickerMaxDate'
import {
  onlyMixpanelTrackingServicesToExclude,
  useLiveEventsTrackerPageAnalyticsEvents,
} from '../../features/live-events/hooks/useLiveEventsTrackerAnalyticsEvents'
import { useLiveEventsTrackingSearchParams } from '../../features/live-events/hooks/useLiveEventsTrackingSearchParams'
import { SortGamesByOptionValue } from '../../features/live-events/hooks/useSortGamesBySelectOptions'
import { useSortedTrackedGames } from '../../features/live-events/hooks/useSortedTrackedGames'
import { useTrackedGames } from '../../features/live-events/hooks/useTrackedGames'
import { highlightEvent, selectHighlightedEvent, selectLiveEventModalState } from '../../features/live-events/slices/liveEventModalSlice'
import {
  gameCalendarVisibilityToggled,
  maxOpenGameCalendarsCount,
  selectSelectedTrackedGames,
  selectVisibleEventTypeGroups,
  selectVisibleGameCalendars,
  toggleEventTypeGroupVisibility,
  trackedGamesSelectionChange,
  visibleGameCalendarsChanged,
} from '../../features/live-events/slices/liveEventsCalendarsSlice'
import { CalendarTimeRanges } from '../../features/live-events/types/Calendar'
import { EventsDataFetchingMode } from '../../features/live-events/types/EventsDataFetchingMode'
import { LiveEventCalendarAdditionalDataId } from '../../features/live-events/types/LiveEventAdditionalCalendarData'
import { LiveEventFeedItem, LiveEventFeedItemType } from '../../features/live-events/types/LiveEventFeedItem'
import { LiveEventTrackerTab } from '../../features/live-events/types/LiveEventTrackerTab'
import {
  PerformanceEffectTimelineItem,
  TrackingEventTimelineItem,
  LiveEventsCalendarTimelineType,
  AnalystReviewTimelineItem,
} from '../../features/live-events/types/LiveEvents'
import { LiveEventsGameAlert, LiveEventsGameAlertType } from '../../features/live-events/types/LiveEventsGameAlert'
import { PerformanceChangesByOptionValue } from '../../features/live-events/types/PerformanceChangesByOption'
import { PerformanceEffectType } from '../../features/live-events/types/PerformanceEffect'
import { TrackedGame, TrackedGameMutation } from '../../features/live-events/types/TrackedGame'
import { TrackingEventTimeRange } from '../../features/live-events/types/TrackingEvents'
import { applyDateRangeToCalendarTimeRanges } from '../../features/live-events/utils/applyDateRangeToCalendarTimeRanges'
import { withFreeLiveEventsReviewStub } from '../../features/live-events/utils/freeLiveEventsReviewStub'
import { useCurrentMarket } from '../../features/markets'
import { GranularityValue } from '../../features/revenue-and-downloads/types/Filters'
import { useFreeLiveEventsGamesSetting } from '../../features/settings'
import { getIntervalByGranularity } from '../../helpers/date'
import { dayMilliseconds } from '../../helpers/dayMilliseconds'
import { useAppDispatch, useAppSelector } from '../../hooks/storeHooks'
import { useDebouncedValue } from '../../hooks/useDebouncedValue'
import { useDocumentTitle } from '../../hooks/useDocumentTitle'
import usePage from '../../hooks/usePage'
import analyticsService from '../../services/AnalyticsService'
import languageService from '../../services/LanguageService'
import PageService from '../../services/PageService'
import utilsService from '../../services/UtilsService'
import { ConfirmDialogData } from '../../types/ConfirmDialogData'
import { LockedFeatureId } from '../../types/LockedFeature'
import './LiveEventsTrackerPage.scss'

const initialTab = LiveEventTrackerTab.Feed
export const maxDayDifferenceForDateRange = 60
export const maxLiveEventsCalendarZoomInMilliseconds = maxDayDifferenceForDateRange * dayMilliseconds // 60 days / 2 months
export const minLiveEventsCalendarZoomInMilliseconds = 7 * dayMilliseconds // 1 week
const hideWhatsNewSection = true

const LiveEventsTrackerPage: React.FC = () => {
  const { t } = useTranslation()
  usePage(PageService.getPageWithId('live-events-tracker'), 'Live Events Tracker')
  useDocumentTitle(t('sidebar:live_events_tracker'))

  const dispatch = useAppDispatch()
  const location = useLocation()
  const navigate = useNavigate()
  const { subpage = initialTab } = useParams<{ subpage: LiveEventTrackerTab }>()
  const { currentMarketIso } = useCurrentMarket()
  const hasAccessToLiveEventsTracker = useLiveEventsTrackerAccessCheck()
  const freeLiveEventsGamesSetting = useFreeLiveEventsGamesSetting()
  const liveEventsSelectedGameIds = useLiveEventsSelectedGameIdsSetting()
  const { updateLiveEventsSelectedGameIdsSetting } = useUpdateLiveEventsSelectedGameIdsSetting()
  const { setSearchParams, parsedParams } = useLiveEventsTrackingSearchParams()
  const trackedGamesQuery = useTrackedGames({})

  const selectedGames = useAppSelector(selectSelectedTrackedGames)
  const selectedGameIds = useMemo(() => selectedGames.map((trackedGame) => trackedGame.game.id), [selectedGames])

  // Convert free live events game IDs to Game objects for passing to child components
  const freeLiveEventsGamesArray = useFreeLiveEventsGamesArray(trackedGamesQuery.data)
  const highlightedEventId = useAppSelector(selectHighlightedEvent)
  const liveEventModalState = useAppSelector(selectLiveEventModalState)
  const [gamePerformanceDialogData, setGamePerformanceDialogData] = useState<GamePerformaceDialogData>()
  const { showModal: showEventModal } = useLiveEventModal()
  const { showModal: showAnalystNotesModal } = useLiveEventsAnalystNotesModal()
  const { showModal: showAnalystOverviewModal } = useLiveEventsAnalystOverviewModal()

  const [confirmRemoveGameDialogData, setConfirmRemoveGameDialogData] = useState<ConfirmDialogData<TrackedGameMutation>>()

  // used to control calendar's timeline range setting to selected date range
  const [calendarTimeRanges, setCalendarTimeRanges] = useState<CalendarTimeRanges>({})

  // Debounce calendar time ranges to prevent multiple API calls during scrolling
  const debouncedCalendarTimeRanges = useDebouncedValue(calendarTimeRanges, 400)

  // Resolve data fetching mode based on current tab
  const dataFetchingMode = useMemo(() => {
    switch (subpage) {
      case LiveEventTrackerTab.Events:
        return EventsDataFetchingMode.EventCalendar
      case LiveEventTrackerTab.Feed:
        return EventsDataFetchingMode.Feed
      case LiveEventTrackerTab.Statistics:
        return EventsDataFetchingMode.EventStats
      case LiveEventTrackerTab.Games:
      default:
        return EventsDataFetchingMode.PerGame
    }
  }, [subpage])

  const isInternalUser = useInternalAccessCheck()

  const trackedEvents = useLiveEventsByCalendarTimeRanges({
    calendarTimeRanges: debouncedCalendarTimeRanges,
    trackedGameIds: selectedGameIds,
    fallbackDateFrom: parsedParams.dateFrom,
    fallbackDateTo: parsedParams.dateTo,
    feedTimeFrom: parsedParams.feedDateFrom,
    feedTimeTo: parsedParams.feedDateTo,
    dataFetchingMode,
  })

  const allEventsMaxEndByGame = useMemo(() => {
    const result: { [gameId: string]: number } = {}
    const futureEventCutoff = isInternalUser ? undefined : endOfDay(new Date()).getTime()

    Object.entries(trackedEvents.events).forEach(([gameId, events]) => {
      const maxEnd = events.reduce((max, e) => {
        if (futureEventCutoff !== undefined && e.start > futureEventCutoff) return max
        const eventEnd = endOfDay(new Date(e.end)).getTime()
        return eventEnd > max ? eventEnd : max
      }, 0)
      if (maxEnd > 0) result[gameId] = maxEnd
    })

    return result
  }, [trackedEvents.events, isInternalUser])

  const loadedEventsTimeRangeByGame = useMemo(() => {
    if (trackedEvents.isFetching) return undefined
    const result: { [gameId: string]: TrackingEventTimeRange } = {}
    Object.entries(trackedEvents.events).forEach(([gameId, events]) => {
      if (events.length === 0) return
      const range = events.reduce(
        (acc, e) => ({
          minTimeData:
            startOfDay(new Date(e.start)).getTime() < acc.minTimeData || acc.minTimeData === 0 ? startOfDay(new Date(e.start)).getTime() : acc.minTimeData,
          maxTimeData: endOfDay(new Date(e.end)).getTime() > acc.maxTimeData ? endOfDay(new Date(e.end)).getTime() : acc.maxTimeData,
        }),
        { minTimeData: 0, maxTimeData: 0 }
      )
      result[gameId] = range
    })
    return result
  }, [trackedEvents.events, trackedEvents.isFetching])

  const visibleGameCalendars = useAppSelector(selectVisibleGameCalendars)
  const visibleEventTypeGroups = useAppSelector(selectVisibleEventTypeGroups)
  const [selectedPerformanceChartInterval, setSelectedPerformanceChartInterval] = useState<Interval>()

  const trackedEventsLoading = trackedEvents.isLoading || trackedEvents.isFetching
  const showEventsFetchingProgressBar =
    trackedEvents.isFetching && !trackedEvents.isLoading && (subpage === LiveEventTrackerTab.Games || subpage === LiveEventTrackerTab.Events)
  const isLoading = trackedGamesQuery.isLoading || trackedGamesQuery.isFetching

  // Use common filters hook
  const { commonFilters, commonFiltersData, filterApplicability, liveEventNamesArray, handleCommonFiltersChange } = useLiveEventsCommonFilters({
    selectedGameIds,
    trackedEvents,
    parsedParams,
    onFiltersChange: (filters) => {
      setSearchParams((currentParams) => ({
        ...currentParams,
        liveEventTags: filters.liveEventTags.map((tag) => tag.id),
        liveEventSecondaryTags: filters.liveEventSecondaryTags.map((tag) => tag.id),
        additionalDatas: Object.keys(filters.additionalDatas).map((key) => key),
        liveEventDurations: Object.keys(filters.liveEventDurations).map((key) => key),
        liveEventAppearances: Object.keys(filters.liveEventAppearances).map((key) => key),
        liveEventPricePoints: Object.keys(filters.liveEventPricePoints).map((key) => key),
        motivations: Object.keys(filters.motivations).map((key) => key),
        archetypes: Object.keys(filters.archetypes).map((key) => key),
      }))
    },
  })

  // Default filters for LiveEventsTrackerPage - all additional data selected, others empty
  const defaultFilters = useMemo(
    () => ({
      liveEventTags: [],
      liveEventSecondaryTags: [],
      additionalDatas: commonFiltersData.additionalDatas.reduce((acc, data) => ({ ...acc, [data.id]: true }), {} as { [dataId: string]: boolean }),
      liveEventDurations: {},
      liveEventAppearances: {},
      liveEventPricePoints: {},
      motivations: {},
      archetypes: {},
    }),
    [commonFiltersData.additionalDatas]
  )

  // Use filtered data hook
  const { filteredTrackingEventsByGame, eventNameSearchValue, handleEventNameSearchValueChange } = useLiveEventsFilteredData({
    selectedGames,
    trackedEvents,
    commonFilters,
    eventNameSearchValue: '',
    performanceEffectThreshold: parsedParams.performanceEffectThreshold,
    performanceChangesBy: parsedParams.performanceChangesBy,
  })

  const maxTrackingEndedForPicker = useMemo(
    () => Object.values(trackedEvents.stats ?? {}).reduce((max, stat) => (stat.trackingEnded > max ? stat.trackingEnded : max), 0),
    [trackedEvents.stats]
  )

  // allEventsMaxEndByGame already applies the non-internal future-event cutoff, so a non-empty
  // result means events visible to the current user are loaded.
  const hasVisibleEvents = useMemo(() => Object.keys(allEventsMaxEndByGame).length > 0, [allEventsMaxEndByGame])

  const pickerResetKey = useMemo(() => `${currentMarketIso}|${[...selectedGameIds].sort().join(',')}`, [currentMarketIso, selectedGameIds])

  // High-water mark per market + selection: moving the date range far into the past narrows the
  // fetched event window, which must not shrink the picker max (GR-2964).
  const maxDateForPicker = useLiveEventsPickerMaxDate({
    isInternalUser,
    maxTimeFromEvents: filteredTrackingEventsByGame.maxTime,
    maxTrackingEnded: maxTrackingEndedForPicker,
    floorToToday: true,
    hasVisibleEvents,
    isFetching: trackedEvents.isLoading || trackedEvents.isFetching,
    resetKey: pickerResetKey,
  })

  const handleCalendarTimeRangeChange = useCallback(
    (calendarId: string | number, timeStart: number, timeEnd: number) => {
      // update time range only if it's within allowed limits
      if (timeEnd - timeStart <= maxLiveEventsCalendarZoomInMilliseconds + dayMilliseconds) {
        setCalendarTimeRanges((current) => {
          const newRanges = { ...current }
          if (newRanges[calendarId]) {
            newRanges[calendarId] = { timeStart, timeEnd }
          } else {
            newRanges[calendarId] = { timeStart: parsedParams.dateFrom, timeEnd: parsedParams.dateTo }
          }

          return newRanges
        })
      }
    },
    [parsedParams.dateFrom, parsedParams.dateTo]
  )

  const tabs = useMemo(() => {
    return Object.values(LiveEventTrackerTab).map((tab) => ({
      id: tab,
      name: t(`live-events:tab_${tab.toLowerCase()}`),
    }))
  }, [t])

  // resolve configuration for filters based on selected tab
  const filterConfig: LiveEventsFiltersConfig = useMemo(() => {
    const filtersDisabled = [LiveEventTrackerTab.Events, LiveEventTrackerTab.Statistics].includes(subpage)
    const config: LiveEventsFiltersConfig = {
      performanceChangesByDisabled: filtersDisabled,
      thresholdDisabled: filtersDisabled,
      sortGamesByDisabled: filtersDisabled,
    }

    return config
  }, [subpage])

  // sort selected games by selected sorting value
  const sortedSelectedGames = useSortedTrackedGames({ trackedGames: selectedGames, sortBy: parsedParams.sortGamesBy })

  // use games previously selected by user if url does not contain any game ids - do this only on initial load
  useEffect(() => {
    if (!liveEventsSelectedGameIds.isLoading && liveEventsSelectedGameIds.data) {
      setSearchParams((current) => ({ ...current, gameIds: current.gameIds.length > 0 ? current.gameIds : liveEventsSelectedGameIds.data || [] }))
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [liveEventsSelectedGameIds.isLoading, setSearchParams])

  // keep any games in the URL that are not yet in user settings (e.g. added via highlight button)
  // NOTE: liveEventsSelectedGameIds.data is intentionally NOT a dep. Mutations elsewhere on this
  // page (clearing/removing games via the dialog or Remove confirmation) update the saved setting
  // synchronously via an RTK Query optimistic cache update, but the URL update goes through
  // setSearchParams + a navigate effect and lands one render later. If we re-ran this effect on
  // saved-data changes, parsedParams.gameIds would still hold the stale (pre-clear) URL ids while
  // savedIds is already empty, and we would incorrectly persist the just-cleared ids back to the
  // user setting. Re-running only when parsedParams.gameIds itself changes guarantees the URL
  // and the saved setting are in sync at the moment we read them. (GR-2870)
  useEffect(() => {
    if (liveEventsSelectedGameIds.isLoading || !liveEventsSelectedGameIds.data || !trackedGamesQuery.data) return
    const savedIds = liveEventsSelectedGameIds.data
    const newIds = parsedParams.gameIds.filter((id) => !savedIds.includes(id))
    if (newIds.length === 0) return
    const newTrackedGames = newIds.map((id) => trackedGamesQuery.data?.find((tg) => tg.game.id === id)).filter(Boolean) as TrackedGame[]
    const savedTrackedGames = savedIds.map((id) => trackedGamesQuery.data?.find((tg) => tg.game.id === id)).filter(Boolean) as TrackedGame[]
    updateLiveEventsSelectedGameIdsSetting([...savedTrackedGames, ...newTrackedGames])
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [parsedParams.gameIds, liveEventsSelectedGameIds.isLoading, trackedGamesQuery.data, updateLiveEventsSelectedGameIdsSetting])

  // resolve actual tracked games from url params
  useEffect(() => {
    let games: TrackedGame[] = []

    // TODO: checking hasAccessToLiveEventsTracker is unnecesary since we only show Feature Locked Card when user does not have the needed role
    if (hasAccessToLiveEventsTracker) {
      games = parsedParams.gameIds
        .map((gameId) => {
          const isFreeGameId = freeLiveEventsGamesArray?.some((g) => g.id === gameId) ?? false
          // For free Live Events games the user does not need a specific tracking permission to see
          // analyst review / AI summary data, so preserve the real TrackedGame (with `review`,
          // `analyst`, `aiSummariesAvailable`, etc.) returned by the backend even when
          // `permission` is false. Falling through to the stub below would drop those fields and
          // disable the LiveOps Summary / Live Events Strategy Changes buttons. (GR-2889)
          const trackedGame = trackedGamesQuery.data?.find((tg) => tg.game.id === gameId && (tg.permission || isFreeGameId))
          // For free LE games the backend's `review` may be missing or unpublished when the user
          // has no explicit tracking permission. Promote it to a published stub so the LiveOps
          // Summary button is enabled; non-free games and already-published reviews are
          // returned unchanged. (GR-2889)
          if (trackedGame) return withFreeLiveEventsReviewStub(trackedGame, freeLiveEventsGamesArray)

          const freeGame = freeLiveEventsGamesArray?.find((g) => g.id === gameId)
          if (freeGame) {
            // No entry in `trackedGamesQuery.data` at all — synthesize a published-stub
            // TrackedGame so the LiveOps Summary button is enabled and the AI button stays
            // enabled by default (`aiSummariesAvailable` is left undefined). (GR-2889)
            return withFreeLiveEventsReviewStub(
              {
                game: freeGame,
                permission: true,
                allowed: true,
                published: true,
                trackingStarted: 0,
                blueprints: [],
              },
              freeLiveEventsGamesArray
            )
          }

          return null
        })
        .filter((game) => !!game) as TrackedGame[]
    } else if (freeLiveEventsGamesSetting?.currentData?.data?.free_live_events_games) {
      const freeGameIds = freeLiveEventsGamesSetting.currentData.data.free_live_events_games as string[]

      games = parsedParams.gameIds
        .map((gameId) => {
          if (!freeGameIds.includes(gameId)) return null

          const trackedGame = trackedGamesQuery.data?.find((tg) => tg.game.id === gameId)
          if (trackedGame) return withFreeLiveEventsReviewStub(trackedGame, freeLiveEventsGamesArray)

          const freeGame = freeLiveEventsGamesArray?.find((g) => g.id === gameId)
          if (freeGame) {
            // Same review-default rationale as the hasAccessToLiveEventsTracker branch above. (GR-2889)
            return withFreeLiveEventsReviewStub(
              {
                game: freeGame,
                permission: true,
                allowed: true,
                published: true,
                trackingStarted: 0,
                blueprints: [],
              },
              freeLiveEventsGamesArray
            )
          }

          return null
        })
        .filter((game) => !!game) as TrackedGame[]
    }

    dispatch(trackedGamesSelectionChange(games))
  }, [
    parsedParams.gameIds,
    trackedGamesQuery.data,
    hasAccessToLiveEventsTracker,
    freeLiveEventsGamesSetting?.currentData?.data,
    freeLiveEventsGamesArray,
    dispatch,
  ])

  // initially set the default date range
  useEffect(() => {
    // TODO: checking hasAccessToLiveEventsTracker is unnecesary since we only show Feature Locked Card when user does not have the needed role
    const startDate = parsedParams.dateFrom ? new Date(parsedParams.dateFrom) : startOfDay(add(new Date(), { days: -14 }))
    const initialStartDate = hasAccessToLiveEventsTracker ? startDate : new Date(lockedLiveEventsTrackerStartTimestamp)
    const endDate = parsedParams.dateTo ? new Date(parsedParams.dateTo) : endOfDay(new Date())
    const initialEndDate = hasAccessToLiveEventsTracker ? endDate : new Date(lockedLiveEventsTrackerEndTimestamp)
    setSearchParams((currentParams) => ({ ...currentParams, dateFrom: initialStartDate.getTime(), dateTo: initialEndDate.getTime() }))
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])

  // initially set the default date range for feed view
  useEffect(() => {
    // TODO: checking hasAccessToLiveEventsTracker is unnecesary since we only show Feature Locked Card when user does not have the needed role
    const startDate = parsedParams.feedDateFrom ? new Date(parsedParams.feedDateFrom) : startOfDay(add(new Date(), { days: -1 }))
    const initialStartDate = hasAccessToLiveEventsTracker ? startDate : new Date(lockedLiveEventsTrackerStartTimestamp)
    const endDate = parsedParams.feedDateTo ? new Date(parsedParams.feedDateTo) : endOfDay(new Date())
    const initialEndDate = hasAccessToLiveEventsTracker ? endDate : new Date(lockedLiveEventsTrackerEndTimestamp)
    setSearchParams((currentParams) => ({ ...currentParams, feedDateFrom: initialStartDate.getTime(), feedDateTo: initialEndDate.getTime() }))
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])

  // set hidden calendars
  useEffect(() => {
    dispatch(visibleGameCalendarsChanged(sortedSelectedGames.map((trackedGame) => trackedGame.game.id)))
  }, [dispatch, sortedSelectedGames])

  // handle navigate and scroll to highlighted event
  useLayoutEffect(() => {
    let timeout: number
    if (liveEventModalState.meta && !isLoading && !trackedEventsLoading && parsedParams.dateFrom && parsedParams.dateTo) {
      const { trackedGameId, eventStartTime, eventType } = liveEventModalState.meta

      // navigate to Live Events Tracker Game tab if not already there
      if (subpage !== LiveEventTrackerTab.Games && subpage !== LiveEventTrackerTab.Events) {
        navigate(`/live-events-tracker/${LiveEventTrackerTab.Games}${location.search}`)
      }

      // Add game to selected games if not already in the live URL
      const liveGameIds = new URLSearchParams(window.location.search).getAll('gameIds').flatMap((v) => v.split(','))
      if (trackedGameId && !liveGameIds.includes(trackedGameId)) {
        setSearchParams((current) => {
          // Prevent the sync effect from re-injecting stale modal params into the URL
          const { gameIds, ...rest } = current as any
          const cleanCurrent = Object.entries(rest).reduce((acc, [key, value]) => {
            if (!key.startsWith('m_') && key !== 'modalType') acc[key] = value
            return acc
          }, {} as any)
          const newGameIds = gameIds.includes(trackedGameId) ? gameIds : [...gameIds, trackedGameId]
          return { ...cleanCurrent, gameIds: newGameIds }
        })
        return
      }

      // Open the game calendar if it's not already open
      if (!visibleGameCalendars?.includes(trackedGameId || '')) {
        dispatch(gameCalendarVisibilityToggled(trackedGameId ? trackedGameId : ''))
      }

      // open the event type group if it's not already open
      if (eventType && !visibleEventTypeGroups?.includes(eventType)) {
        dispatch(toggleEventTypeGroupVisibility(eventType))
      }

      // make sure the dialogs get closed
      setGamePerformanceDialogData(undefined)

      // reset the selected performance chart interval
      setSelectedPerformanceChartInterval(undefined)

      timeout = window.setTimeout(() => {
        // scroll calendar viewport to instance
        const calendarId = subpage === LiveEventTrackerTab.Games ? trackedGameId : compareEventsCalendarId
        if (eventStartTime && calendarId) {
          const { timeStart, timeEnd } = calendarTimeRanges[calendarId] || { timeStart: parsedParams.dateFrom, timeEnd: parsedParams.dateTo }
          const delta = (timeEnd || 0) - (timeStart || 0)
          const halfDelta = Math.round(delta / 2)
          setCalendarTimeRanges((current) => {
            current[calendarId] = { timeStart: eventStartTime - halfDelta, timeEnd: eventStartTime + halfDelta }

            return { ...current }
          })
        }

        // scroll to element (middle of viewport).
        // Group DOM ids differ between tabs:
        //   - Games tab (LiveEventsCalendarTrackingEventGroup): `${gameId}-${typeId}`
        //   - Compare Events tab (LiveEventsCalendarTrackingEventWithGameGroup): `${gameId}-${typeId}_${gameId}`
        // because Compare-Events groups are keyed per (typeId, gameId). Try both so the
        // window scroll works on both tabs.
        const highlightedTrackedGameId = liveEventModalState?.meta?.trackedGameId
        const highlightedEventType = liveEventModalState?.meta?.eventType
        const element =
          highlightedTrackedGameId && highlightedEventType
            ? document.getElementById(`${highlightedTrackedGameId}-${highlightedEventType}`) ||
              document.getElementById(`${highlightedTrackedGameId}-${highlightedEventType}_${highlightedTrackedGameId}`)
            : undefined
        const rect = element?.getBoundingClientRect()
        const topHeaderHeight = 148

        if (element) {
          window.scrollTo({ top: (rect?.top || 0) + window.scrollY - topHeaderHeight - document.documentElement.clientHeight / 2, behavior: 'smooth' })
        }

        // reset higlight state
        dispatch(highlightEvent({ id: highlightedEventId }))
      }, liveEventModalState.meta.timeout || 0)
    }

    return () => clearTimeout(timeout)
  }, [
    calendarTimeRanges,
    dispatch,
    highlightedEventId,
    liveEventModalState,
    location.search,
    navigate,
    parsedParams.dateFrom,
    parsedParams.dateTo,
    parsedParams.gameIds,
    isLoading,
    trackedEventsLoading,
    visibleGameCalendars,
    location.pathname,
    visibleEventTypeGroups,
    subpage,
    setSearchParams,
  ])

  const handleGameSelectionChange = useCallback(
    (games: TrackedGame[], hdGameSelected: boolean) => {
      const newGameIds = games.map((trackedGame) => trackedGame.game.id)
      const currentGameIds = selectedGameIds

      // Check if game selection actually changed
      const gameSelectionChanged = newGameIds.length !== currentGameIds.length || !newGameIds.every((id) => currentGameIds.includes(id))

      setSearchParams((currentParams) => {
        const baseParams = {
          ...currentParams,
          gameIds: newGameIds,
        }

        // Only reset filters when game selection actually changes
        if (gameSelectionChanged) {
          return hdGameSelected
            ? {
                ...baseParams,
                liveEventTags: [],
                liveEventSecondaryTags: [],
                motivations: [],
                archetypes: [],
              }
            : {
                ...baseParams,
                liveEventPricePoints: [],
                liveEventTags: [],
                liveEventSecondaryTags: [],
                motivations: [],
                archetypes: [],
              }
        }

        return baseParams
      })

      // update the selected tracked game ids to user settings, these will be reflected to the url in a useEffect hook above
      updateLiveEventsSelectedGameIdsSetting(games)
    },
    [setSearchParams, updateLiveEventsSelectedGameIdsSetting, selectedGameIds]
  )

  const handleCalendarEventItemClick = useCallback(
    (clickedItem: TrackingEventTimelineItem | PerformanceEffectTimelineItem | AnalystReviewTimelineItem) => {
      switch (clickedItem.type) {
        case LiveEventsCalendarTimelineType.TrackingEvent:
        case LiveEventsCalendarTimelineType.TrackingEventWithGame:
          const item: TrackingEventTimelineItem = clickedItem as TrackingEventTimelineItem

          showEventModal({
            trackedGameId: item.trackedGame.game.id,
            eventTypeId: item.trackingEvent.typeId,
            eventId: item.trackingEventId,
            durationStart: item.trackingEvent.start,
            durationEnd: item.trackingEvent.end,
            tab: EventDialogTab.Description,
          })
          break

        case LiveEventsCalendarTimelineType.PerformanceEffect:
          const data = {
            gameName:
              (clickedItem as PerformanceEffectTimelineItem).trackedGame.game.names['us'] ||
              (clickedItem as PerformanceEffectTimelineItem).trackedGame.game.resolvedName,
            gameAppId: (clickedItem as PerformanceEffectTimelineItem).trackedGame.game.appId,
            gameConventionalSubgenre: languageService.getTranslation(
              'conventionalSubgenres',
              (clickedItem as PerformanceEffectTimelineItem).trackedGame.game.conventionalSubgenreId
            ),
            source: `${clickedItem.group} Change Click`,
          }

          analyticsService.trackEvent('Live Events Tracker: Opened Game Statistics Graph', {
            data,
            serviceToExclude: onlyMixpanelTrackingServicesToExclude,
          })

          const yAxisDataTypes = resolveYAxisDataTypes((clickedItem as PerformanceEffectTimelineItem).group)
          setSelectedPerformanceChartInterval({ start: clickedItem.start_time, end: clickedItem.end_time })
          setGamePerformanceDialogData({
            appId: (clickedItem as PerformanceEffectTimelineItem).trackedGame.game.appId,
            gameName: (clickedItem as PerformanceEffectTimelineItem).trackedGame.game.resolvedName,
            marketIso: currentMarketIso,
            highlightedTimestamp: clickedItem.start_time,
            initialYAxisLeftConfig: { dataType: yAxisDataTypes[0] },
            initialYAxisRightConfig: { dataType: yAxisDataTypes[1] },
            initialGranularity:
              (clickedItem as PerformanceEffectTimelineItem).group === PerformanceEffectType.MAU ? GranularityValue.Month : GranularityValue.Day,
          })
          break
        case LiveEventsCalendarTimelineType.AnalystReview:
          const analystReviewItem: AnalystReviewTimelineItem = clickedItem as AnalystReviewTimelineItem
          showAnalystNotesModal({
            trackedGameId: analystReviewItem.review.gameId,
            commentId: analystReviewItem.review.id,
          })
      }
    },
    [currentMarketIso, showAnalystNotesModal, showEventModal]
  )

  const handleGameAlertCardClick = useCallback(
    (alert: LiveEventsGameAlert) => {
      switch (alert.type) {
        case LiveEventsGameAlertType.Event:
          showEventModal({
            trackedGameId: alert.gameId,
            eventId: alert.eventId || '',
            eventTypeId: alert.eventTypeId || '',
            tab: EventDialogTab.Description,
          })
          break

        case LiveEventsGameAlertType.Comment:
          showAnalystNotesModal({
            trackedGameId: alert.gameId,
            commentId: alert.commentId || '',
          })
          break
      }
    },
    [showAnalystNotesModal, showEventModal]
  )

  const handleCalendarEventGroupClick = useLiveEventsCalendarGroupClickHandler()
  const handleCalendarBlueprintClick = useLiveEventsCalendarBlueprintClickHandler(trackedEvents.events)

  // make sure selected date starts from start of day and ends to end of day
  const handleDateRangeChange = useCallback(
    (value?: DateRangeValue, reason?: DateRangeChangeReason) => {
      let fromDate = value?.fromDate
      let toDate = value?.toDate

      // Handle fromDate and toDate setting to have maxDayDifferenceForDateRange.
      // We clamp the inclusive day count to maxDayDifferenceForDateRange so that the
      // timespan picker, calendar viewport (capped at maxLiveEventsCalendarZoomInMilliseconds)
      // and Event Stats graph all visualize the same number of days.
      //
      // `getDayDifferenceFromTwoDates` returns `floor((to - from) / dayMs)`. For a
      // [start-of-day, end-of-day] range this equals `inclusiveDays - 1`, so the
      // threshold for "needs clamping" is `>= maxDayDifferenceForDateRange` (i.e.
      // strictly more than `maxDayDifferenceForDateRange - 1` whole days). Using `>`
      // here would let exactly-`maxDayDifferenceForDateRange + 1` inclusive day spans
      // through, which is what GR-2491 reproduced when shifting a boundary by one
      // day on an already 60-day range.
      if (value && value.fromDate && value.toDate) {
        const dayDifference = utilsService.getDayDifferenceFromTwoDates(value.fromDate.getTime(), value.toDate.getTime())
        if (dayDifference >= maxDayDifferenceForDateRange) {
          if (new Date(parsedParams.dateFrom as number).getTime() === fromDate?.getTime() && toDate) {
            // toDate changed: keep toDate at end-of-day, clamp fromDate so the inclusive
            // span equals maxDayDifferenceForDateRange days.
            fromDate = new Date(toDate.getTime())
            fromDate.setHours(0, 0, 0, 0)
            fromDate.setDate(fromDate.getDate() - (maxDayDifferenceForDateRange - 1))
          } else if (new Date(parsedParams.dateTo as number)?.getTime() === toDate?.getTime() && fromDate) {
            // fromDate changed: keep fromDate at start-of-day, clamp toDate so the inclusive
            // span equals maxDayDifferenceForDateRange days. toDate must be end-of-day to
            // match the picker convention applied elsewhere.
            toDate = new Date(fromDate.getTime())
            toDate.setHours(23, 59, 59, 999)
            toDate.setDate(fromDate.getDate() + (maxDayDifferenceForDateRange - 1))
          }
        }

        if (fromDate && toDate) {
          const fixedDayDifference = utilsService.getDayDifferenceFromTwoDates(fromDate.getTime(), toDate.getTime())
          const data = {
            dayDifference: fixedDayDifference,
          }

          analyticsService.trackEvent('Live Events Tracker: Date Range Day Difference Changed', {
            data,
            serviceToExclude: onlyMixpanelTrackingServicesToExclude,
          })
        }
      }

      if (parsedParams.dateFrom !== fromDate?.getTime() || parsedParams.dateTo !== toDate?.getTime()) {
        setSearchParams((currentParams) => ({ ...currentParams, dateFrom: fromDate?.getTime(), dateTo: toDate?.getTime() }))
      }

      // User picker changes always move the calendar. Automatic bounds clamps (history load
      // dropping picker max to the last tracked event) only move calendars still on the previous
      // picker range, so scrolling to a later empty month is not yanked back (GR-2985).
      setCalendarTimeRanges((currentRanges) =>
        applyDateRangeToCalendarTimeRanges(
          currentRanges,
          fromDate?.getTime(),
          toDate?.getTime(),
          reason === 'bounds' ? { onlyIfInSyncWith: { dateFrom: parsedParams.dateFrom, dateTo: parsedParams.dateTo } } : undefined
        )
      )
    },
    [parsedParams.dateFrom, parsedParams.dateTo, setSearchParams]
  )

  // make sure selected date starts from start of day and ends to end of day for feed view
  const handleFeedDateRangeChange = useCallback(
    (value?: DateRangeValue) => {
      let fromDate = value?.fromDate
      let toDate = value?.toDate

      // Handle fromDate and toDate setting to have maxDayDifferenceForDateRange
      if (value && value.fromDate && value.toDate && fromDate && toDate) {
        const fixedDayDifference = utilsService.getDayDifferenceFromTwoDates(fromDate.getTime(), toDate.getTime())
        const data = {
          dayDifference: fixedDayDifference,
        }

        analyticsService.trackEvent('Live Events Tracker: Date Range Day Difference Changed for Feed View', {
          data,
          serviceToExclude: onlyMixpanelTrackingServicesToExclude,
        })
      }

      if (parsedParams.feedDateFrom !== fromDate?.getTime() || parsedParams.feedDateTo !== toDate?.getTime()) {
        setSearchParams((currentParams) => ({ ...currentParams, feedDateFrom: fromDate?.getTime(), feedDateTo: toDate?.getTime() }))
      }
    },
    [parsedParams.feedDateFrom, parsedParams.feedDateTo, setSearchParams]
  )

  const handleSortGamesByChange = useCallback(
    (value: SortGamesByOptionValue) => {
      setSearchParams((currentParams) => ({ ...currentParams, sortGamesBy: value }))
    },
    [setSearchParams]
  )

  const handlePerformanceEffectThresholdChange = useCallback(
    (value: number) => {
      setSearchParams((currentParams) => ({ ...currentParams, performanceEffectThreshold: value }))
    },
    [setSearchParams]
  )

  const handlePerformanceChangesByChange = useCallback(
    (value: PerformanceChangesByOptionValue) => {
      setSearchParams((currentParams) => ({ ...currentParams, performanceChangesBy: value }))
    },
    [setSearchParams]
  )

  const handleGameRemoveDialogData = useCallback(
    (trackedGame: TrackedGame, hdGameSelected: boolean) => {
      setConfirmRemoveGameDialogData({
        title: t('live-events:remove_game'),
        confirmText: <Trans i18nKey={'live-events:remove_selected_game_confirm'} values={{ gameName: trackedGame.game.name }} />,
        destructiveAction: true,
        actionText: t('common:remove'),
        data: { ...trackedGame, hdGameSelected: hdGameSelected },
      })
    },
    [t]
  )

  const handleDeselectGame = useCallback(
    (dialogData?: ConfirmDialogData<TrackedGameMutation>) => {
      if (dialogData) {
        if (dialogData.data?.hdGameSelected) {
          setSearchParams((currentParams) => ({
            ...currentParams,
            liveEventTags: [],
            liveEventSecondaryTags: [],
            motivations: [],
            archetypes: [],
            gameIds: currentParams.gameIds.filter((gameId) => gameId !== dialogData?.data?.game.id),
          }))
        } else {
          setSearchParams((currentParams) => ({
            ...currentParams,
            liveEventTags: [],
            liveEventPricePoints: [],
            liveEventSecondaryTags: [],
            motivations: [],
            archetypes: [],
            gameIds: currentParams.gameIds.filter((gameId) => gameId !== dialogData?.data?.game.id),
          }))
        }
        updateLiveEventsSelectedGameIdsSetting(selectedGames.filter((existingTrackedGame) => existingTrackedGame.game.id !== dialogData?.data?.game.id))
      }

      setConfirmRemoveGameDialogData(undefined)
    },
    [setSearchParams, updateLiveEventsSelectedGameIdsSetting, selectedGames]
  )

  const handleOverviewTrackedGameChanged = useCallback(
    (trackedGame: TrackedGame) => {
      showAnalystOverviewModal({
        trackedGameId: trackedGame.game.id,
      })
    },
    [showAnalystOverviewModal]
  )

  const handleOpenFeedItemClick = useCallback(
    (feedItem: LiveEventFeedItem, tab: EventDialogTab = EventDialogTab.Description) => {
      switch (feedItem.type) {
        case LiveEventFeedItemType.LiveEvent:
          showEventModal({
            trackedGameId: feedItem.trackedGame?.game.id || '',
            eventTypeId: feedItem.trackingEvent?.typeId || '',
            eventId: feedItem.trackingEvent?.eventId || '',
            durationStart: feedItem.start,
            durationEnd: feedItem.end,
            tab,
          })
          break

        case LiveEventFeedItemType.AnalystNote:
          if (feedItem.analystNote && feedItem.trackedGame) {
            showAnalystNotesModal({
              trackedGameId: feedItem.trackedGame?.game.id,
              commentId: feedItem.analystNote.id,
            })
          }
          break
      }
    },
    [showAnalystNotesModal, showEventModal]
  )

  const handleChartClick = setSelectedPerformanceChartInterval

  const dateRange = useMemo(
    () => ({
      fromDate: parsedParams.dateFrom ? new Date(parsedParams.dateFrom) : undefined,
      toDate: parsedParams.dateTo ? new Date(parsedParams.dateTo) : undefined,
    }),
    [parsedParams.dateFrom, parsedParams.dateTo]
  )

  const dateRangeForFeed = useMemo(
    () => ({
      fromDate: parsedParams.feedDateFrom ? new Date(parsedParams.feedDateFrom) : undefined,
      toDate: parsedParams.feedDateTo ? new Date(parsedParams.feedDateTo) : undefined,
    }),
    [parsedParams.feedDateFrom, parsedParams.feedDateTo]
  )

  const gamePerformanceDialogSelectedUpdates = useMemo(() => {
    return selectedPerformanceChartInterval?.start ? [{ timestamp: selectedPerformanceChartInterval?.start }] : []
  }, [selectedPerformanceChartInterval?.start])

  useLiveEventsTrackerPageAnalyticsEvents(subpage, selectedGames, parsedParams.performanceChangesBy, eventNameSearchValue)

  const getPerformanceDialogInstanceTableInterval = useCallback(
    (granularity: GranularityValue) => {
      return selectedPerformanceChartInterval
        ? selectedPerformanceChartInterval
        : gamePerformanceDialogData?.highlightedTimestamp
        ? getIntervalByGranularity(granularity, gamePerformanceDialogData?.highlightedTimestamp)
        : undefined
    },
    [gamePerformanceDialogData?.highlightedTimestamp, selectedPerformanceChartInterval]
  )

  const handleGamePerformanceDialogOpen = useCallback(
    (trackedGame: TrackedGame) => {
      setGamePerformanceDialogData({
        appId: trackedGame.game.appId,
        gameName: trackedGame.game.resolvedName,
        marketIso: currentMarketIso,
        initialYAxisLeftConfig: { dataType: PerformanceChartV2DataType.Revenue },
        initialYAxisRightConfig: { dataType: PerformanceChartV2DataType.Downloads },
        initialGranularity: GranularityValue.Week,
      })
    },
    [currentMarketIso]
  )

  const handlePerformanceDialogClose = useCallback(() => {
    setGamePerformanceDialogData(undefined)
    setSelectedPerformanceChartInterval(undefined)
  }, [])

  const handleGroupByBlueprintChanged = useCallback(() => {
    setSearchParams((currentParams) => ({ ...currentParams, groupByBlueprint: !currentParams.groupByBlueprint }))
  }, [setSearchParams])

  const handleHideDiscontinuedBlueprintsChanged = useCallback(() => {
    setSearchParams((currentParams) => ({ ...currentParams, hideDiscontinuedBlueprints: !currentParams.hideDiscontinuedBlueprints }))
  }, [setSearchParams])

  const filterSelectedGame = useMemo(() => {
    return selectedGames.find((game) => game.game.appId === gamePerformanceDialogData?.appId)
  }, [gamePerformanceDialogData?.appId, selectedGames])

  return (
    <div className="LiveEventsTrackerPage">
      {hasAccessToLiveEventsTracker ? (
        <>
          <Box className="LiveEventsTrackerPage__Header" mb={2}>
            <Grid container spacing={2} alignItems={'center'}>
              <Grid item xs>
                {t('live-events:page_description')}
              </Grid>
              <Grid item>
                <ShareUrlButton />
              </Grid>
            </Grid>
          </Box>

          <div className="LiveEventsTrackerPage__Content">
            <Card sx={{ borderBottomLeftRadius: 0, borderBottomRightRadius: 0 }}>
              <LiveEventsGameSelector
                selectedGames={selectedGames}
                onGameSelectionChange={handleGameSelectionChange}
                freeLiveEventsGames={freeLiveEventsGamesArray}
              />
              <Divider />

              {isLoading && <GRCircularProgress m={2} />}

              {sortedSelectedGames.length > 0 && (
                <CardContent>
                  <LiveEventsViewFilters
                    selectedGames={selectedGames}
                    commonFilters={commonFilters}
                    commonFiltersData={commonFiltersData}
                    filterApplicability={filterApplicability}
                    defaultFilters={defaultFilters}
                    eventNameSearchValue={eventNameSearchValue}
                    liveEventNames={liveEventNamesArray}
                    onFiltersChange={handleCommonFiltersChange}
                    onEventNameSearchValueChange={handleEventNameSearchValueChange}
                  />
                </CardContent>
              )}
              <Divider light />
            </Card>

            <GRTabNavigation
              tabs={tabs}
              selectedTab={tabs.findIndex((tab) => tab.id === subpage)}
              pagePrefix={'/live-events-tracker'}
              query={location.search}
              sticky={true}
              hasSecondaryStickyElement={true}
              isLoading={showEventsFetchingProgressBar}
            />

            {!isLoading && selectedGames.length === 0 && (
              <Box margin={'20px'} style={{ textAlign: 'center' }}>
                <Typography>{t('live-events:no_games_selected_info')}</Typography>
              </Box>
            )}

            {isLoading ? (
              <GRCircularProgress m={2} />
            ) : (
              selectedGames.length > 0 &&
              parsedParams.dateFrom &&
              parsedParams.dateTo && (
                <>
                  {!hideWhatsNewSection && (
                    <TabContext value={subpage}>
                      <TabPanel value={LiveEventTrackerTab.Games} style={{ padding: '0px' }}>
                        <LiveEventsGameAlertsList
                          trackedGames={sortedSelectedGames}
                          trackingEventsByGame={filteredTrackingEventsByGame}
                          showComments={commonFilters.additionalDatas[LiveEventCalendarAdditionalDataId.AnalystReview] ? true : false}
                          onLiveEventsGameAlertCardClick={handleGameAlertCardClick}
                          isLoading={trackedEventsLoading}
                        />
                      </TabPanel>
                    </TabContext>
                  )}

                  <Box mt={2}>
                    {(subpage === LiveEventTrackerTab.Games || subpage === LiveEventTrackerTab.Events || subpage === LiveEventTrackerTab.Statistics) && (
                      <LiveEventsFilters
                        dateRange={dateRange}
                        minDate={defaultTrackedEventsStartTimestamp}
                        maxDate={maxDateForPicker}
                        sortGamesBy={parsedParams.sortGamesBy}
                        threshold={parsedParams.performanceEffectThreshold}
                        performanceChangesBy={parsedParams.performanceChangesBy}
                        groupByBlueprint={parsedParams.groupByBlueprint}
                        hideDiscontinuedBlueprints={parsedParams.hideDiscontinuedBlueprints}
                        onDateRangeChange={handleDateRangeChange}
                        onSortGamesByChange={handleSortGamesByChange}
                        onThresholdChange={handlePerformanceEffectThresholdChange}
                        onPerformanceChangesByChange={handlePerformanceChangesByChange}
                        onGroupByBlueprintChanged={handleGroupByBlueprintChanged}
                        onHideDiscontinuedBlueprintsChanged={handleHideDiscontinuedBlueprintsChanged}
                        config={filterConfig}
                      />
                    )}

                    {subpage === LiveEventTrackerTab.Feed && (
                      <LiveEventsFeedFilters
                        dateRange={dateRangeForFeed}
                        minDate={defaultTrackedEventsStartTimestamp}
                        maxDate={maxDateForPicker}
                        onDateRangeChange={handleFeedDateRangeChange}
                      />
                    )}
                  </Box>

                  <TabContext value={subpage}>
                    <TabPanel value={LiveEventTrackerTab.Feed}>
                      <LiveEventsFeed
                        trackedGames={sortedSelectedGames}
                        trackingEventsByGame={filteredTrackingEventsByGame}
                        dateRange={dateRangeForFeed}
                        isLoading={trackedEventsLoading}
                        selectedCalendarAdditionalDataIdsMap={commonFilters.additionalDatas}
                        onOpenFeedItemClick={handleOpenFeedItemClick}
                      />
                    </TabPanel>
                    <TabPanel value={LiveEventTrackerTab.Games}>
                      <LiveEventsCalendarList
                        trackedGames={sortedSelectedGames}
                        trackingEventsByGame={filteredTrackingEventsByGame}
                        stats={trackedEvents.stats}
                        allEventsMaxEndByGame={allEventsMaxEndByGame}
                        loadedEventsTimeRangeByGame={loadedEventsTimeRangeByGame}
                        calendarTimeRanges={calendarTimeRanges}
                        onEventItemClick={handleCalendarEventItemClick}
                        onEventGroupClick={handleCalendarEventGroupClick}
                        onBlueprintClick={handleCalendarBlueprintClick}
                        onDeselectGame={handleGameRemoveDialogData}
                        onOverviewTrackedGameChanged={handleOverviewTrackedGameChanged}
                        timeStart={parsedParams.dateFrom}
                        timeEnd={parsedParams.dateTo}
                        isLoading={trackedEvents.isLoading}
                        highlightedEventId={highlightedEventId}
                        onCalendarTimeRangeChange={handleCalendarTimeRangeChange}
                        selectedCalendarAdditionalDataIdsMap={commonFilters.additionalDatas}
                        minZoom={minLiveEventsCalendarZoomInMilliseconds}
                        maxZoom={maxLiveEventsCalendarZoomInMilliseconds}
                        onGamePerformanceDialogOpen={handleGamePerformanceDialogOpen}
                        onGroupByBlueprintChanged={handleGroupByBlueprintChanged}
                        groupByBlueprint={parsedParams.groupByBlueprint}
                        hideDiscontinuedBlueprints={parsedParams.hideDiscontinuedBlueprints}
                      />
                    </TabPanel>
                    <TabPanel value={LiveEventTrackerTab.Events}>
                      {sortedSelectedGames.length > maxOpenGameCalendarsCount && (
                        <Box mb={1}>
                          <Grid container spacing={1}>
                            <Grid item>
                              <Info color="action" />
                            </Grid>
                            <Grid item xs>
                              <Typography color="action" variant="body1">
                                {t('live-events:compare_events_over_3_games_usability_instructions')}
                              </Typography>
                            </Grid>
                          </Grid>
                        </Box>
                      )}

                      <LiveEventsCalendarByEventTypeContainer
                        trackedGames={sortedSelectedGames}
                        trackingEventsByGame={filteredTrackingEventsByGame}
                        stats={trackedEvents.stats}
                        allEventsMaxEndByGame={allEventsMaxEndByGame}
                        loadedEventsTimeRangeByGame={loadedEventsTimeRangeByGame}
                        calendarTimeRanges={calendarTimeRanges}
                        onItemClick={handleCalendarEventItemClick}
                        onGroupClick={handleCalendarEventGroupClick}
                        onBlueprintClick={handleCalendarBlueprintClick}
                        timeStart={parsedParams.dateFrom}
                        timeEnd={parsedParams.dateTo}
                        isLoading={trackedEvents.isLoading}
                        highlightedEventId={highlightedEventId}
                        groupByBlueprint={parsedParams.groupByBlueprint}
                        onCalendarTimeRangeChange={handleCalendarTimeRangeChange}
                        minZoom={minLiveEventsCalendarZoomInMilliseconds}
                        maxZoom={maxLiveEventsCalendarZoomInMilliseconds}
                      />
                    </TabPanel>
                    <TabPanel value={LiveEventTrackerTab.Statistics}>
                      <LiveEventsEventStatistics
                        trackedGames={sortedSelectedGames}
                        trackingEventsByGame={filteredTrackingEventsByGame}
                        timeStart={parsedParams.dateFrom}
                        timeEnd={parsedParams.dateTo}
                        isLoading={trackedEventsLoading}
                      />
                    </TabPanel>
                  </TabContext>
                </>
              )
            )}

            {gamePerformanceDialogData && (
              <GamePerformanceDialog
                open={!!gamePerformanceDialogData}
                onClose={handlePerformanceDialogClose}
                appId={gamePerformanceDialogData!.appId}
                marketIso={gamePerformanceDialogData!.marketIso}
                gameName={gamePerformanceDialogData!.gameName}
                selectedVerticalMarks={gamePerformanceDialogSelectedUpdates}
                initialYAxisLeftConfig={gamePerformanceDialogData.initialYAxisLeftConfig}
                initialYAxisRightConfig={gamePerformanceDialogData.initialYAxisRightConfig}
                initialGranularity={gamePerformanceDialogData.initialGranularity}
                onChartClick={handleChartClick}
                selectedGame={filterSelectedGame}
                showNewEventsStrip
                render={(granularity: GranularityValue, dateRange) => {
                  return (
                    <PerformanceDialogEventsContainer
                      interval={getPerformanceDialogInstanceTableInterval(granularity)}
                      dateRange={dateRange}
                      parentEvents={trackedEvents.events}
                      trackedGames={trackedGamesQuery.data}
                      appId={gamePerformanceDialogData.appId}
                      isLoading={trackedGamesQuery.isFetching || trackedEventsLoading}
                    />
                  )
                }}
              ></GamePerformanceDialog>
            )}
          </div>
        </>
      ) : (
        <LockedFeature.Card lockedFeatureId={LockedFeatureId.LiveEventsTracker} />
      )}

      <ConfirmDialog open={!!confirmRemoveGameDialogData} confirmDialogData={confirmRemoveGameDialogData} onClose={handleDeselectGame} />
    </div>
  )
}

export default LiveEventsTrackerPage

export const resolveYAxisDataTypes = (value: PerformanceEffectType): [PerformanceChartV2DataType, PerformanceChartV2DataType] => {
  switch (value) {
    case PerformanceEffectType.DAU:
      return [PerformanceChartV2DataType.DAU, PerformanceChartV2DataType.Revenue]
    case PerformanceEffectType.MAU:
      return [PerformanceChartV2DataType.MAU, PerformanceChartV2DataType.Revenue]
    default:
      return [PerformanceChartV2DataType.Revenue, PerformanceChartV2DataType.Downloads]
  }
}
